import { Add } from "@mui/icons-material";
import {
  Button,
  Card,
  CircularProgress,
  DialogActions,
  DialogContent,
  Grid,
  Stack,
  Switch,
  Tooltip,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CustomDialog from "components/shared/Dialog/CustomDialog";
import { DeviceStatus } from "components/utils/enums";
import { handleDownloadCsv, handlePrintCsv } from "components/utils/handlers";
import theme from "components/utils/theme";
import { useEffect, useMemo, useState, useCallback } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  sendToSocket,
} from "../../../../redux/saga/socket";
import { deviceUsersActions } from "../../../../redux/slice/deviceUsers.slice";
import { devicesActions } from "../../../../redux/slice/devices.slice";
import { GET_SECUREBOOT_DEVICE_USERS } from "../../../../redux/types/shared.types";
import CustomTitle from "../../../shared/CustomTitle";
import MyTable from "../../../shared/Tables/MyTable/MyTable";
import MyTableToolbar from "../../../shared/Tables/MyTable/MyTableToolbar";
import table from "../../../shared/Tables/table.module.scss";
import DeviceUserPopup from "../DeviceUserPopup/DeviceUserPopup";
import {
  getDeviceUserColumns,
  UBUNTU_USERS_DOWNLOAD_COLS,
  WINDOWS_USERS_DOWNLOAD_COLS,
} from "../helper";
import CustomButton from "components/shared/Buttons/CustomButton";
import { useLocation, useNavigate } from "react-router-dom";
import { authActions } from "../../../../redux/slice/auth.slice";
import { accessActions } from "../../../../redux/slice/access.slice";
import { checkSinglePermission } from "components/shared/helper";
import { USER_PERMISSIONS } from "../../Management/AccessManagement/helper";
import { registerDeviceInviteService } from "../../../../redux/services/auth.service";
import { toggleSecurebootStatusServ } from "../../../../redux/services/single.service";

const SecurebootSwitch = styled((props) => (
  <Switch focusVisibleClassName="Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 46,
  height: 24,
  padding: 0,
  display: "flex",
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(22px)",
      color: theme.palette.common.white,
      "& + .MuiSwitch-track": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? theme.palette.success.dark
            : theme.palette.success.main,
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: theme.palette.success.light,
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color:
        theme.palette.mode === "light"
          ? theme.palette.grey[100]
          : theme.palette.grey[600],
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: theme.palette.mode === "light" ? 0.7 : 0.3,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 20,
    height: 20,
    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
  },
  "& .MuiSwitch-track": {
    borderRadius: 24 / 2,
    backgroundColor:
      theme.palette.mode === "light"
        ? theme.palette.grey[300]
        : theme.palette.grey[700],
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 200,
    }),
  },
}));

const DeviceUsers = () => {
  const deviceUsers = useSelector(deviceUsersActions.selectors.getdeviceUsers);
  const currentDevice = useSelector(devicesActions.selectors.getSelectedDevice);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const { id } = useParams();
  const dispatch = useDispatch();
  const [setttingPopup, setSettingsPopup] = useState(false);
  const [selectedUser, setSelectedUser] = useState({});
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", username: "" });
  const [inviteLoading, setInviteLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = useMemo(
    () => `${location.pathname}${location.search}`,
    [location.pathname, location.search]
  );
  const currentUser = useSelector(authActions.selectors.getCurrentUser);
  const userPermissions = useSelector(
    accessActions.selectors.getUserPermissions
  );

  const handleViewActivities = useCallback(
    (row) => {
      if (!row?._id && !row?.id) {
        toast.error("User details not available");
        return;
      }

      const url = `/secureboot-user-activity/${row?._id || row?.id}`;
      navigate(`${url}?context=${encodeURIComponent(currentPath)}`);
    },
    [currentPath, navigate]
  );

  const handleInviteDialogOpen = () => {
    if (currentDevice?.status === DeviceStatus.disconnected) {
      return toast.error("Device is Disconnected");
    }
    setInviteForm({ email: "", username: "" });
    setInviteLoading(false);
    setInviteDialogOpen(true);
  };

  const handleInviteDialogClose = () => {
    setInviteDialogOpen(false);
    setInviteForm({ email: "", username: "" });
    setInviteLoading(false);
  };

  const handleInviteFieldChange = (field) => (event) => {
    let { value } = event.target;

    if (field === "username") {
      value = value.replace(/[^A-Za-z0-9]/g, "").slice(0, 10);
    }

    setInviteForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSendInvite = async () => {
    const trimmedEmail = inviteForm.email.trim().toLowerCase();
    const trimmedUsername = inviteForm.username.trim();
    const emailRegex = /^\S+@\S+\.\S+$/;

    if (!trimmedEmail || !emailRegex.test(trimmedEmail)) {
      return toast.error("Please enter a valid email address");
    }

    if (!trimmedUsername) {
      return toast.error("Please enter a username");
    }

    if (inviteLoading) {
      return;
    }

    setInviteLoading(true);

    try {
      await registerDeviceInviteService({
        deviceId: id,
        email: trimmedEmail,
        username: trimmedUsername,
      });
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || "Failed to send invite";
      toast.error(errorMessage);
      setInviteLoading(false);
      return;
    }

    const payload = {
      deviceId: id,
      uid: currentDevice?.uid,
      os: currentDevice?.systemDetails?.os,
      email: trimmedEmail,
      username: trimmedUsername,
    };

    sendToSocket("INVITE_DEVICE_USER", payload);
    toast.success("Invite sent successfully");
    handleInviteDialogClose();
    setInviteLoading(false);
  };

  const handleSettingsPopupOpen = useCallback((row) => {
    setSelectedUser(row);
    setSettingsPopup(true);
  }, []);

  const handleSettingsPopupClose = (row) => {
    setSelectedUser({});
    setSettingsPopup(false);
  };

  const handleToggleSecureboot = useCallback(
    async (row, checked) => {
      const identifier = row?._id || row?.id;
      const username = row?.username;
      const previousValue = Boolean(row?.securebootEnabled);

      if (!identifier || !username) {
        toast.error("Missing user details for secureboot toggle");
        return;
      }

      dispatch(
        deviceUsersActions.actions.updateDeviceUser({
          identifier,
          changes: { securebootEnabled: checked },
        })
      );

      try {
        await toggleSecurebootStatusServ({
          deviceId: id,
          username,
          enable: checked,
        });

        toast.success(
          `Secureboot ${checked ? "enabled" : "disabled"} successfully`
        );
      } catch (error) {
        dispatch(
          deviceUsersActions.actions.updateDeviceUser({
            identifier,
            changes: { securebootEnabled: previousValue },
          })
        );

        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to update Secureboot status";
        toast.error(message);
      }
    },
    [dispatch, id]
  );

  useEffect(() => {
    if (id) {
      // clear previous rows so we don't show stale data while loading
      dispatch(deviceUsersActions.actions.setdeviceUsers([]));
      const payload = {
        deviceId: id,
      };
      setSecurebootLoading(true);
      dispatch({ type: GET_SECUREBOOT_DEVICE_USERS, payload });
    }
  }, [dispatch, id]);

  const handleUserSearch = (term) => {
    setUserSearchTerm(term);
  };

  // useEffect(() => {
  //   setUpDeviceUsersSocket(dispatch);
  //   return () => {
  //     unSubscribeSocketTopics([TOPICS.USERS]);
  //   };
  // }, [dispatch]);



  const filteredUsers = useMemo(() => {
    if (!userSearchTerm) return deviceUsers;
    const regex = new RegExp(userSearchTerm, "i");
    return deviceUsers.filter((alert) =>
      Object.values(alert).some((value) => regex.test(value))
    );
  }, [deviceUsers, userSearchTerm]);

  const [securebootLoading, setSecurebootLoading] = useState(false);

  // stop loading when deviceUsers have been updated for this device
  useEffect(() => {
    if (securebootLoading && deviceUsers) {
      setSecurebootLoading(false);
    }
  }, [deviceUsers, securebootLoading]);
  const selectedDevice = useSelector(
    devicesActions.selectors.getSelectedDevice
  );
  const deviceUserColumns = useMemo(() => {
    const baseColumns = [];

    if (Array.isArray(deviceUsers) && deviceUsers[0] && deviceUsers[0].email) {
      baseColumns.push(
        { field: "email", headerName: "Email", width: 200, flex: 1 },
        { field: "username", headerName: "Username", width: 160, flex: 1 },
        { field: "createdAt", headerName: "Created At", width: 160, flex: 1 },
        { field: "lastLogin", headerName: "Last Login", width: 160, flex: 1 }
      );
    } else {
      baseColumns.push(
        ...getDeviceUserColumns(
          selectedDevice?.systemDetails?.os,
          handleSettingsPopupOpen
        )
      );
    }

    baseColumns.push({
      field: "activity",
      headerName: "Activity",
      width: 150,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
          <Tooltip
            placement="top"
            arrow
            title={
              params.row?.hasSessions
                ? "View session activity"
                : "No sessions recorded"
            }
          >
          <Button
            size="small"
            variant="text"
              disabled={!params.row?.hasSessions}
            onClick={() => handleViewActivities(params.row)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Check Activity
          </Button>
        </Tooltip>
      ),
    });

    baseColumns.push({
      field: "actions",
      headerName: "Actions",
      width: 140,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const isEnabled = Boolean(params?.row?.securebootEnabled);
        const tooltipTitle = isEnabled
          ? "Disable Secureboot"
          : "Enable Secureboot";

        return (
          <Tooltip placement="top" arrow title={tooltipTitle}>
            <SecurebootSwitch
              checked={isEnabled}
              onChange={(event) =>
                handleToggleSecureboot(params.row, event.target.checked)
              }
            />
          </Tooltip>
        );
      },
    });

    return baseColumns;
  }, [
    selectedDevice?.systemDetails?.os,
    deviceUsers,
    handleSettingsPopupOpen,
    handleToggleSecureboot,
    handleViewActivities,
  ]);

  const deviceUserDownloadColumns = useMemo(() => {
    return selectedDevice?.systemDetails?.os === "Ubuntu"
      ? UBUNTU_USERS_DOWNLOAD_COLS
      : WINDOWS_USERS_DOWNLOAD_COLS;
  }, [selectedDevice?.systemDetails?.os]);

  return (
    <div>
      <CustomDialog
        borderRadius={"10px"}
        maxWidth={"md"}
        title={"Invite User"}
        handleToggle={handleInviteDialogClose}
        isOpen={inviteDialogOpen}
        fullWidth
      >
        <DialogContent
          sx={{
            p: 0,
            paddingLeft: "36px",
            paddingRight: "36px",
            paddingTop: "28px",
            paddingBottom: "12px",
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Send a device invite by providing the recipient&apos;s email and the
            username they should use on the device.
          </Typography>
          <Stack spacing={2.5}>
            <TextField
              fullWidth
              label="Username"
              value={inviteForm.username}
              onChange={handleInviteFieldChange("username")}
              placeholder="Enter username"
              helperText="Letters and numbers only, max 10 characters"
              inputProps={{ maxLength: 10 }}
              margin="dense"
            />
            <TextField
              autoFocus
              fullWidth
              label="Email"
              value={inviteForm.email}
              onChange={handleInviteFieldChange("email")}
              placeholder="name@domain.com"
              type="email"
              margin="dense"
            />
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            p: 0,
            paddingLeft: "36px",
            paddingRight: "36px",
            paddingBottom: "28px",
            paddingTop: "16px",
            gap: 1.5,
          }}
        >
          <CustomButton
            onClick={handleInviteDialogClose}
            label={"Cancel"}
            padding={"0 40px"}
            color={theme.palette.primary.main}
            bgColor={theme.palette.background.default}
            hover={theme.palette.gray.light}
          />
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <CustomButton
              onClick={handleSendInvite}
              label={"Send Invite"}
              padding={"0 40px"}
              bgColor={theme.palette.primary.main}
              hover={theme.palette.primary.light}
              disabled={inviteLoading}
            />
            {inviteLoading && <CircularProgress size={20} />}
          </Stack>
        </DialogActions>
      </CustomDialog>
      <CustomDialog
        borderRadius={"10px"}
        fullScreen={true}
        title={"Manage Device User Setting"}
        handleToggle={handleSettingsPopupClose}
        isOpen={setttingPopup}
      >
        <DeviceUserPopup selectedUser={selectedUser} />
      </CustomDialog>
      <Grid
        my={"15px"}
        container
        sx={{ justifyContent: { xl: "space-between", md: "center" } }}
        spacing={2}
      ></Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} xl={12} className={table.tableContainer}>
          <CustomTitle title={"Users"} count={deviceUsers?.length} />
          <Card>
            <MyTableToolbar
              placeHolder={"Search For Users"}
              onSearch={handleUserSearch}
              onDownload={() =>
                handleDownloadCsv({
                  rows: filteredUsers,
                  columns: deviceUserDownloadColumns,
                  name: `${selectedDevice?.deviceName}_device_users`,
                })
              }
              onPrint={() =>
                handlePrintCsv({
                  rows: filteredUsers,
                  columns: deviceUserDownloadColumns,
                  name: `${selectedDevice?.deviceName}_device_users`,
                })
              }
              isAlerts={false}
              btn1Txt={
                checkSinglePermission(
                  USER_PERMISSIONS.ADD_DEVICE_USER,
                  userPermissions,
                  currentUser?.role
                )
                  ? "Invite User"
                  : null
              }
              handleBtn1Click={handleInviteDialogOpen}
              btn1Icon={Add}
            />
            <MyTable
              checkboxSelection={false}
              columns={deviceUserColumns}
              rows={filteredUsers}
              externalLoading={securebootLoading}
            />
          </Card>
        </Grid>
      </Grid>
      <br />
    </div>
  );
};

export default DeviceUsers;
